import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import EventTimelineBarChart from 'components/EventTimelineBarChart';
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import Api from "pages/AInsight/Operations/util/api";

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';

const DEFAULT_DISPLAY_COUNT = 2;
const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const DeviceRebootDrawer = ({ chartData, auth }) => {
  const theme = useTheme();
  const { sendBatch } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [deviceData, setDeviceData] = useState([]);
  const [requestUuids] = useState({
    devices: uuidv4(),
  });

  const fetchData = async () => {
    if (!auth && checkMockup !== 'true') return;
    setLoading(true);
    const api = Api.init(auth || {});
    const devicesParams = api.getDeviceRebootDevicesParams();

    const { getSendBatchData } = await sendBatch([
      { ...devicesParams, requestUuid: requestUuids.devices },
    ]);

    const devicesRes = getSendBatchData({ requestUuid: requestUuids.devices })?.data;
    if (devicesRes?.devices) {
      setDeviceData(devicesRes.devices);
    } else if (devicesRes?.data?.devices) {
      // Fallback for different data structures
      setDeviceData(devicesRes.data.devices);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth]);

  const tableData = useMemo(() =>
    showAll ? deviceData : deviceData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAll, deviceData]);

  const columns = useMemo(() => [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("rebootCount", {
      header: "Count",
      cell: (info) => (
        <Typography sx={{ color: '#172664', fontSize: '13px' }}>
          {info.getValue()}
        </Typography>
      ),
    }),
  ], [columnHelper]);

  const table = useTableInstance({
    tableName: "topRebootersTable",
    columns: columns,
    data: tableData,
    enableSorting: false,
    enablePagination: false,
  });

  const barChartData = useMemo(() => {
    if (!chartData || !chartData.series) return null;
    const now = new Date();
    const startHourTs = new Date(now).setMinutes(0, 0, 0, 0) - 24 * 60 * 60 * 1000;

    return {
      series: [
        {
          name: 'Reboots',
          data: chartData.series
            .map(d => {
              // 確保將 UTC 字串正確轉為 Local Timestamp
              const ts = typeof d[0] === 'string' && !d[0].endsWith('Z') && !d[0].includes('+')
                ? new Date(d[0] + 'Z').getTime()
                : new Date(d[0]).getTime();
              return [ts, d[1]];
            })
            .filter(d => d[0] >= startHourTs)
        }
      ]
    };
  }, [chartData]);

  const showMore = deviceData.length > DEFAULT_DISPLAY_COUNT;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_linebar_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          Device Reboot Events
        </Typography>
      </Box>

      <CollapseCard title="Unexpected Reboot">
        <Box sx={{ width: "100%", height: "250px", pt: 2 }}>
          {barChartData ? (
            <EventTimelineBarChart barChartData={barChartData} height="200px" barWidth={12} />
          ) : (
            <Typography>No data available</Typography>
          )}
        </Box>
      </CollapseCard>

      <CollapseCard title="Top Devices by Reboots (Last 24 Hours)">
        <Box sx={{ width: "100%" }}>
          <TableField {...table} loading={loading} />
          {showMore && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Typography
                onClick={() => setShowAll(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0,
                  padding: 0,
                  color: theme.palette.primary.greySecound,
                  cursor: "pointer",
                }}
              >
                {showAll ? "Show Less" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default DeviceRebootDrawer;
